import type { VisualSpec } from '@numbernode/contracts';

export type DataRow = Record<string, unknown>;

const quantitative = { field: 'value', type: 'quantitative' as const, title: null };
const nominal = { field: 'dimension', type: 'nominal' as const, title: null, sort: '-x' as const };

export function toVegaLite(spec: VisualSpec, rows: DataRow[]): Record<string, unknown> {
  const base = {
    $schema: 'https://vega.github.io/schema/vega-lite/v6.json',
    width: 'container',
    height: 'container',
    autosize: { type: 'fit', contains: 'padding' },
    data: { values: rows },
    title: { text: spec.title, anchor: 'start', fontSize: 15 },
    config: {
      view: { stroke: null },
      axis: { labelLimit: 140, titleFontWeight: 500 },
      legend: { orient: 'bottom' },
    },
    params: [{ name: 'selection', select: { type: 'point', fields: ['dimension'], on: 'click', clear: 'dblclick' } }],
  };
  switch (spec.chartType) {
    case 'line': return { ...base, mark: { type: 'line', point: true, tooltip: true }, encoding: { x: { ...nominal, sort: null }, y: quantitative, color: spec.color ? { field: spec.color, type: 'nominal' } : undefined } };
    case 'area': return { ...base, mark: { type: 'area', line: true, point: true, tooltip: true }, encoding: { x: { ...nominal, sort: null }, y: quantitative } };
    case 'scatter': return { ...base, mark: { type: 'point', filled: true, tooltip: true, size: 90 }, encoding: { x: { field: 'dimension', type: 'quantitative' }, y: quantitative } };
    case 'histogram': return { ...base, mark: { type: 'bar', tooltip: true }, encoding: { x: { field: 'dimension', type: 'quantitative', bin: true }, y: { aggregate: 'count', type: 'quantitative' } } };
    case 'heatmap': return { ...base, mark: { type: 'rect', tooltip: true }, encoding: { x: nominal, color: quantitative } };
    case 'boxplot': return { ...base, mark: { type: 'boxplot', extent: 'min-max' }, encoding: { x: nominal, y: quantitative } };
    case 'bar':
    default: return { ...base, mark: { type: 'bar', cornerRadiusEnd: 3, tooltip: true }, encoding: { x: quantitative, y: nominal, opacity: { condition: { param: 'selection', value: 1 }, value: 0.45 } } };
  }
}

export function toECharts(spec: VisualSpec, rows: DataRow[]): Record<string, unknown> {
  if (spec.chartType === 'treemap') {
    return {
      title: { text: spec.title, left: 12, top: 8, textStyle: { fontSize: 15 } },
      tooltip: { trigger: 'item' },
      series: [{ type: 'treemap', roam: false, nodeClick: false, data: rows.map((row) => ({ name: String(row.dimension ?? ''), value: Number(row.value ?? 0) })), label: { show: true, formatter: '{b}\n{c}' } }],
    };
  }
  return {
    title: { text: spec.title, left: 12, top: 8, textStyle: { fontSize: 15 } },
    tooltip: { trigger: 'axis' },
    grid: { left: 45, right: 20, top: 55, bottom: 55 },
    xAxis: { type: 'category', data: rows.map((row) => row.dimension), axisLabel: { rotate: rows.length > 12 ? 35 : 0 } },
    yAxis: { type: 'value' },
    series: [{ type: spec.chartType === 'line' ? 'line' : 'bar', data: rows.map((row) => row.value), smooth: true, large: rows.length > 2000 }],
  };
}
